'use client'

import { useEffect, useMemo, useRef } from 'react'

import { experimentsTrackEvent } from '../eventTracking'
import {
  EXPERIMENTS_TRACK_EVENT_LOGIN_PASSWORD_FAILURE,
  EXPERIMENTS_TRACK_EVENT_LOGIN_PASSWORD_PAGE_VIEW,
  EXPERIMENTS_TRACK_EVENT_LOGIN_PASSWORD_SUBMIT,
  EXPERIMENTS_TRACK_EVENT_LOGIN_SWITCH_TO_OTP,
} from '../eventTracking/EXPERIMENTS_TRACK_EVENT'
import { useGetExperiment } from '../hooks'
import { EXPERIMENT_NAME_LOGIN_TWO_STEP } from '../model/EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from '../model/EXPERIMENT_VARIATION'
import { type ExperimentType } from '../model/Experiment.types'

import {
  type LoginPasswordTracking,
  type LoginPasswordTrackingAction,
} from './loginPasswordTracking'

const eventByAction: Record<LoginPasswordTrackingAction, string> = {
  failure: EXPERIMENTS_TRACK_EVENT_LOGIN_PASSWORD_FAILURE,
  page_view: EXPERIMENTS_TRACK_EVENT_LOGIN_PASSWORD_PAGE_VIEW,
  submit: EXPERIMENTS_TRACK_EVENT_LOGIN_PASSWORD_SUBMIT,
  switch_to_otp: EXPERIMENTS_TRACK_EVENT_LOGIN_SWITCH_TO_OTP,
}

const resolveTwoStepLoginPasswordTracking = (
  experiment: Pick<ExperimentType, 'isEnabled' | 'variant'>,
): LoginPasswordTracking => ({
  track: (action): void => {
    if (
      !experiment.isEnabled ||
      experiment.variant !== EXPERIMENT_VARIATION.V1
    ) {
      return
    }

    experimentsTrackEvent(eventByAction[action])
  },
})

type TrackingFlag = { current: boolean }

const trackTwoStepLoginPasswordPageView = (
  tracking: LoginPasswordTracking,
  hasTrackedPageView: TrackingFlag,
): void => {
  if (hasTrackedPageView.current) {
    return
  }

  hasTrackedPageView.current = true
  tracking.track('page_view')
}

export const useTwoStepLoginPasswordTracking = (): LoginPasswordTracking => {
  const hasTrackedPageView = useRef(false)
  const { isEnabled, variant } = useGetExperiment(
    EXPERIMENT_NAME_LOGIN_TWO_STEP,
  )
  const tracking = useMemo(
    () => resolveTwoStepLoginPasswordTracking({ isEnabled, variant }),
    [isEnabled, variant],
  )

  useEffect(() => {
    trackTwoStepLoginPasswordPageView(tracking, hasTrackedPageView)
  }, [tracking])

  return tracking
}
